import React, { useEffect, useRef, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Coins, LogOut } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useOrg } from '../contexts/OrgContext';
import { supabase } from '../lib/supabase';
import { ACCOUNT_MODULES, avatarUrlOf, displayNameOf, initialsOf, modulesForRole } from '../lib/appModules';
import { avulsosFromLedger } from '../lib/avulsosCredits';

const ROLE_LABEL: Record<string, string> = { admin: 'Administrador', fin_ops: 'FinOps', employee: 'Funcionário' };

/** Round avatar at the top right; opens the account / management / sign-out menu. */
export default function UserMenu({ floating = false }: { floating?: boolean }) {
  const { user } = useAuth();
  const { org, role } = useOrg();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [avulsos, setAvulsos] = useState<{ spent: number; total: number } | null>(null);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => { setOpen(false); }, [location.pathname]);

  useEffect(() => {
    if (!open) return;
    const onDown = (e: PointerEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('pointerdown', onDown);
    window.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onDown);
      window.removeEventListener('keydown', onKey);
    };
  }, [open]);

  useEffect(() => {
    if (!open || !org?.id) return;
    let cancel = false;
    (async () => {
      const { data, error } = await supabase
        .from('credit_transactions')
        .select('amount, type, created_at')
        .eq('org_id', org.id)
        .order('created_at', { ascending: true })
        .limit(10000);
      if (cancel || error || !data) return;
      setAvulsos(avulsosFromLedger(data, Number(org.purchased_credits) || 0));
    })();
    return () => { cancel = true; };
  }, [open, org?.id, org?.purchased_credits]);

  const meta = user?.user_metadata as Record<string, unknown> | undefined;
  const name = displayNameOf(meta, user?.email);
  const photo = avatarUrlOf(meta);
  const isAutonomous = (org?.plans?.name || '').toLowerCase() === 'individual';
  const items = modulesForRole(ACCOUNT_MODULES, role, isAutonomous);
  const fmt = (n: number) => n.toLocaleString('pt-BR', { maximumFractionDigits: 2 });
  const credits = fmt(org?.available_credits ?? 0);
  const avulsosPct = avulsos && avulsos.total > 0 ? Math.min(1, avulsos.spent / avulsos.total) : 0;

  return (
    <div className={`user-menu${floating ? ' user-menu--floating' : ''}`} ref={ref}>
      <button
        type="button"
        className="user-menu-btn"
        onClick={() => setOpen((o) => !o)}
        aria-haspopup="menu"
        aria-expanded={open}
        aria-label="Conta"
        title={name}
      >
        <Avatar photo={photo} name={name} size={34} />
      </button>
      {open && (
        <div className="user-menu-pop" role="menu">
          <div className="user-menu-head">
            <Avatar photo={photo} name={name} size={40} />
            <div className="user-menu-who">
              <strong>{name}</strong>
              {user?.email && <span>{user.email}</span>}
            </div>
          </div>
          <div className="user-menu-org">
            <span className="user-menu-orgname">{org?.name || '—'}</span>
            {role && <span className="user-menu-role">{ROLE_LABEL[role] || 'Usuário'}</span>}
            <span className="user-menu-credits" title="Créditos disponíveis"><Coins size={13} /> {credits}</span>
            {avulsos && (
              <div className="user-menu-avulsos">
                <div className="user-menu-avulsos-head">
                  <span>Avulsos gastos</span>
                  <strong>{fmt(avulsos.spent)} de {fmt(avulsos.total)}</strong>
                </div>
                <div
                  className="ui-progress-track"
                  role="progressbar"
                  aria-label="Créditos avulsos gastos"
                  aria-valuemin={0}
                  aria-valuemax={avulsos.total}
                  aria-valuenow={avulsos.spent}
                >
                  <div className="ui-progress-fill" style={{ transform: `scaleX(${avulsosPct})` }} />
                </div>
              </div>
            )}
          </div>
          <nav className="user-menu-list">
            {items.map((m) => (
              <Link
                key={m.id}
                to={m.id}
                role="menuitem"
                className={`user-menu-item${location.pathname === m.id ? ' active' : ''}`}
              >
                {m.icon}
                <span>{m.title}</span>
              </Link>
            ))}
          </nav>
          <button type="button" role="menuitem" className="user-menu-item user-menu-logout" onClick={() => { void supabase.auth.signOut(); }}>
            <LogOut size={18} />
            <span>Sair</span>
          </button>
        </div>
      )}
    </div>
  );
}

export function Avatar({ photo, name, size }: { photo: string; name: string; size: number }) {
  const [broken, setBroken] = useState(false);
  useEffect(() => { setBroken(false); }, [photo]);
  const style = { width: size, height: size, fontSize: Math.round(size * 0.38) };
  if (photo && !broken) {
    return <img className="user-avatar" src={photo} alt="" style={style} onError={() => setBroken(true)} referrerPolicy="no-referrer" />;
  }
  return <span className="user-avatar user-avatar--initials" style={style} aria-hidden="true">{initialsOf(name)}</span>;
}
